import { AiAssistantIcon } from '@/assets/static/icons/ai-assistant';
import FormattingTooltip from '@/components/basicComponents/tooltip/dollarsTooltip';
import ShowText from '@/components/dueDiligence-page/dueDiligenceRecord/showText';
import { decimalToPercentage } from '@/components/dueDiligence-page/dueDiligenceRecord/utils';
import { getEnumKeyByValue } from '@/shared/utils/enums';
import {
  extractAndFormatDate,
  extractAndFormatDollar,
  extractAndFormatNumber,
  extractAndFormatPercentage,
  updateMarkdownImageUrls
} from '@/shared/utils/fieldEditor';
import { safeJsonParse } from '@/shared/utils/json';
import { jsonEditorTheme } from '@/styles/theme';
import {
  headingsPlugin,
  imagePlugin,
  linkDialogPlugin,
  linkPlugin,
  listsPlugin,
  MDXEditor,
  MDXEditorMethods,
  tablePlugin,
  thematicBreakPlugin
} from '@mdxeditor/editor';
import { AIFieldType } from '@trace-8/trace8-schema';
import { format } from 'date-fns';
import { JsonEditor } from 'json-edit-react';
import { useEffect, useRef } from 'react';
import styled, { useTheme } from 'styled-components';
import { FieldValueProps } from '../types';
import { getAiAnswer, getAiCitationsCount, getFieldType, hasAiAnswer, isCommentaryNull, isValueNull } from '../utils/fieldUtils';

export const FieldValue = ({ field, index, mode, customRenderer }: FieldValueProps) => {
  const theme = useTheme();
  const editorRef = useRef<MDXEditorMethods>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (editorRef.current && getFieldType(field) === getEnumKeyByValue(AIFieldType, AIFieldType.LONG_FORM_CONTENT)) {
      const markdown =
        isValueNull(field) && hasAiAnswer(field)
          ? updateMarkdownImageUrls(getAiAnswer(field), field.attachments || [])
          : updateMarkdownImageUrls(field.value || '', field.attachments || []);
      editorRef.current.setMarkdown(markdown);
    }
  }, [field]);

  useEffect(() => {
    if (containerRef.current) {
      const links: NodeListOf<HTMLAnchorElement> = containerRef.current.querySelectorAll('a');
      links.forEach((link: HTMLAnchorElement) => {
        link.setAttribute('target', '_blank');
      });
    }
  }, [field]);

  if (customRenderer) {
    return <>{customRenderer(field, index)}</>;
  }

  const fieldType = getFieldType(field);
  const aiAnswer = getAiAnswer(field);
  const citationsCount = getAiCitationsCount(field);

  const renderAiAnswer = (answerJson?: string) => (
    <AiRowCellWrap>
      <AiIconWrap>
        <AiAssistantIcon width={28} height={28} fill={theme.font.base} />
      </AiIconWrap>
      <AiAnswer>
        <>
          <span style={{ fontWeight: 700 }}>Suggested {citationsCount >= 2 && `(${citationsCount - 1} more)`}:</span>{' '}
          {renderFieldValue(fieldType, answerJson || aiAnswer, true)}
        </>
      </AiAnswer>
    </AiRowCellWrap>
  );

  const renderFieldValue = (type: string, value: string, isAi: boolean = false) => {
    switch (type) {
      case getEnumKeyByValue(AIFieldType, AIFieldType.LONG_FORM_CONTENT):
        if (isValueNull(field) && !isAi) {
          return hasAiAnswer(field) ? renderAiAnswer() : <EmptyTodoRowCell>To Do</EmptyTodoRowCell>;
        }
        const content = (
          <div ref={containerRef}>
            <CustomMDXEditor
              ref={editorRef}
              markdown={updateMarkdownImageUrls(value || '', field.attachments || [])}
              readOnly
              suppressHtmlProcessing={true}
              plugins={[
                listsPlugin(),
                headingsPlugin(),
                linkPlugin(),
                linkDialogPlugin(),
                tablePlugin(),
                thematicBreakPlugin(),
                imagePlugin({
                  disableImageSettingsButton: true,
                  disableImageResize: true
                })
              ]}
            />
          </div>
        );

        return <FieldValueWrapper>{mode === 'view' ? content : <ShowText>{content}</ShowText>}</FieldValueWrapper>;

      case getEnumKeyByValue(AIFieldType, AIFieldType.SHORT_TEXT):
        if (isValueNull(field) && !isAi) {
          return hasAiAnswer(field) ? renderAiAnswer() : <EmptyTodoRowCell>To Do</EmptyTodoRowCell>;
        }
        return <FieldValueWrapper>{value}</FieldValueWrapper>;

      case getEnumKeyByValue(AIFieldType, AIFieldType.NUMBER):
        if (isValueNull(field) && isCommentaryNull(field) && !isAi) {
          return hasAiAnswer(field) ? renderAiAnswer() : <EmptyTodoRowCell>To Do</EmptyTodoRowCell>;
        }
        const numberValue = isAi ? extractAndFormatNumber(value) : field.value;
        const numberCommentary = isAi ? value : field.commentary;
        return (
          <FieldValueWrapper>
            {numberValue && numberCommentary
              ? `${numberValue} | ${numberCommentary}`
              : (numberValue && numberValue) || (numberCommentary && numberCommentary)}
          </FieldValueWrapper>
        );

      case getEnumKeyByValue(AIFieldType, AIFieldType.DOLLAR):
        if (isValueNull(field) && isCommentaryNull(field) && !isAi) {
          return hasAiAnswer(field) ? renderAiAnswer() : <EmptyTodoRowCell>To Do</EmptyTodoRowCell>;
        }
        const dollarValue = isAi ? extractAndFormatDollar(value) : field.value;
        const dollarCommentary = isAi ? value : field.commentary;
        return (
          <FieldValueWrapper>
            {dollarValue && dollarCommentary ? (
              <>
                <FormattingTooltip zIndex={1000}>{+dollarValue}</FormattingTooltip> | {dollarCommentary}
              </>
            ) : (
              (dollarValue && <FormattingTooltip zIndex={1000}>{+dollarValue}</FormattingTooltip>) || (dollarCommentary && dollarCommentary)
            )}
          </FieldValueWrapper>
        );

      case getEnumKeyByValue(AIFieldType, AIFieldType.PERCENTAGE):
        if (isValueNull(field) && isCommentaryNull(field) && !isAi) {
          return hasAiAnswer(field) ? renderAiAnswer() : <EmptyTodoRowCell>To Do</EmptyTodoRowCell>;
        }
        const percentageValue = isAi ? extractAndFormatPercentage(value) : field.value;
        const percentageCommentary = isAi ? value : field.commentary;
        return (
          <FieldValueWrapper>
            {percentageValue && percentageCommentary
              ? `${decimalToPercentage(percentageValue as string)} | ${percentageCommentary}`
              : (percentageValue && decimalToPercentage(percentageValue as string)) || (percentageCommentary && percentageCommentary)}
          </FieldValueWrapper>
        );

      case getEnumKeyByValue(AIFieldType, AIFieldType.DATE):
        if (isValueNull(field) && isCommentaryNull(field) && !isAi) {
          return hasAiAnswer(field) ? renderAiAnswer() : <EmptyTodoRowCell>To Do</EmptyTodoRowCell>;
        }
        const dateValue = isAi ? extractAndFormatDate(value) : field.value;
        const dateCommentary = isAi ? value : field.commentary;
        return (
          <FieldValueWrapper>
            {dateValue && dateCommentary
              ? `${format(new Date(dateValue), 'LLL d, yyyy')} | ${dateCommentary}`
              : (dateValue && format(new Date(dateValue), 'LLL d, yyyy')) || (dateCommentary && dateCommentary)}
          </FieldValueWrapper>
        );

      case getEnumKeyByValue(AIFieldType, AIFieldType.JSON):
        if (isValueNull(field) && !isAi) {
          return hasAiAnswer(field) ? renderAiAnswer(field?.AI?.answerJson) : <EmptyTodoRowCell>To Do</EmptyTodoRowCell>;
        }

        const parsedValue = safeJsonParse(value);

        const jsonEditorContent = (
          <JsonEditor data={parsedValue} maxWidth="100%" viewOnly={true} theme={jsonEditorTheme} enableClipboard={false} rootName="" />
        );

        return <FieldValueWrapper>{parsedValue ? jsonEditorContent : value}</FieldValueWrapper>;

      default:
        return <FieldValueWrapper>{value}</FieldValueWrapper>;
    }
  };

  return renderFieldValue(fieldType, field.value || '');
};

const AiRowCellWrap = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
`;

const AiIconWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  height: 100%;
`;

const AiAnswer = styled.div`
  padding-left: 10px;
  border-left: 1px solid ${({ theme }) => theme.border.base};
`;

const EmptyTodoRowCell = styled.div`
  font-weight: 300;
  font-size: 16px;
  line-height: 22px;
  color: ${({ theme }) => theme.font.weak};
`;

const FieldValueWrapper = styled.div`
  font-weight: 400;
  font-size: 16px;
  color: ${({ theme }) => theme.font.base};
  width: 100%;
  overflow-wrap: anywhere;
`;

const CustomMDXEditor = styled(MDXEditor)`
  font-family: Blinker;
  font-size: 16px;
  .mdxeditor-root-contenteditable div {
    padding: 0;
  }
  .mdxeditor-root-contenteditable ul,
  .mdxeditor-root-contenteditable ol {
    padding-left: 26px;
  }
  .mdxeditor-root-contenteditable img {
    max-width: 100%;
    height: auto;
  }
  .mdxeditor-root-contenteditable th,
  .mdxeditor-root-contenteditable td {
    border: 1px solid var(--baseBgActive);
  }
  button[title='Delete image'][type='button'] {
    display: none !important;
  }
`;
